import { type KeyboardEvent, type MouseEvent } from "react";

import { useEditorState } from "@tiptap/react";
import { CheckIcon, PencilIcon, Trash2Icon, XIcon } from "lucide-react";

import { Button, ButtonGroup } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";

import { useEditorContext } from "../../editor/EditorContext";
import { useFloatingToolbar } from "./context";
import { shouldShowFileSection } from "./predicates";

/**
 * File rename / remove controls for the unified floating toolbar:
 * - selected file → edit / remove
 * - edit → name input + accept / cancel
 */
export const FileSection = () => {
  const { editor, commands } = useEditorContext();
  const { features, mode, idle, startEditFile, setFileDraft } =
    useFloatingToolbar();

  const fileState = useEditorState({
    editor,
    selector: ({ editor: instance }) => {
      if (!instance || instance.isDestroyed) {
        return { name: "", canShow: false };
      }
      const name = (instance.getAttributes("file").name as string) || "";
      // Ignore focus here: while renaming, focus moves to the input and
      // clearing draft would abort edit mode.
      return {
        name,
        canShow: shouldShowFileSection(instance, instance.state, {
          requireFocus: false,
        }),
      };
    },
  });

  const name = fileState?.name ?? "";
  const canShow = fileState?.canShow ?? false;

  const isEditing = mode.type === "editFile" && canShow;
  const draft = mode.type === "editFile" ? mode.draft : "";

  // Focus once when the input mounts (edit mode opened).
  const focusInput = (node: HTMLInputElement | null) => {
    if (!node) {
      return;
    }
    node.focus({ preventScroll: true });
    node.select();
  };

  if (!features.file || !editor || !canShow) {
    return null;
  }

  // Hide compact chrome while another expanded panel is active.
  if (mode.type === "editLink") {
    return null;
  }

  const startEditing = () => {
    startEditFile(name);
  };

  const cancelEditing = () => {
    idle();
    if (!editor.isDestroyed) {
      editor.commands.focus();
    }
  };

  const submitName = () => {
    const trimmed = draft.trim();
    idle();
    if (!trimmed || trimmed === name) {
      if (!editor.isDestroyed) {
        editor.commands.focus();
      }
      return;
    }
    commands.updateFile?.({ name: trimmed });
  };

  const removeFile = () => {
    commands.removeFile?.();
    idle();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitName();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEditing();
    }
  };

  if (isEditing) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <Input
          ref={focusInput}
          type="text"
          value={draft}
          placeholder="Enter file name..."
          onChange={(e) => setFileDraft(e.target.value)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          size="small"
          style={{ width: 280 }}
        />
        <ButtonGroup variant="outline" size="small">
          <Button
            type="button"
            aria-label="Apply file name"
            onMouseDown={(e: MouseEvent) => e.preventDefault()}
            onClick={submitName}
            variant="outline"
          >
            <Icon icon={CheckIcon} />
          </Button>
          <Button
            type="button"
            aria-label="Cancel"
            onMouseDown={(e: MouseEvent) => e.preventDefault()}
            onClick={cancelEditing}
            variant="outline"
          >
            <Icon icon={XIcon} />
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  return (
    <ButtonGroup size="small">
      <Button
        aria-label="Rename file"
        onMouseDown={(e: MouseEvent) => e.preventDefault()}
        onClick={startEditing}
        variant="outline"
      >
        <Icon icon={PencilIcon} />
      </Button>
      <Button
        aria-label="Remove file"
        onMouseDown={(e: MouseEvent) => e.preventDefault()}
        onClick={removeFile}
        variant="outline"
      >
        <Icon icon={Trash2Icon} />
      </Button>
    </ButtonGroup>
  );
};
